/**
 * Registers Trace-specific FlowGraph blocks and declaration mappings with
 * Babylon's KHR_interactivity pipeline.
 *
 * Call registerTraceBlocks() once at app init, before any GLB loading.
 * This enables Babylon's KHR_interactivity loader to compile trace ops
 * into the correct block implementations alongside standard KHR ops.
 *
 * Trace ops are registered under the "TRACE_interactivity" glTF extension
 * namespace. In KHR_interactivity JSON, declarations use:
 *   { op: "setActive", extension: "TRACE_interactivity" }
 * In our internal GraphData format, we use the flat form:
 *   { op: "trace/setActive" }
 * The KHR converters handle the translation.
 */

import { addToBlockFactory } from "@babylonjs/core/FlowGraph/Blocks/flowGraphBlockFactory";
import { addNewInteractivityFlowGraphMapping } from "@babylonjs/loaders/glTF/2.0/Extensions/KHR_interactivity/declarationMapper.js";
import { PointerSetBlock } from "./blocks/PointerSetBlock";
import { PointerGetBlock } from "./blocks/PointerGetBlock";
import { PointerInterpolateBlock } from "./blocks/PointerInterpolateBlock";
import { TraceSetActiveBlock } from "./blocks/TraceSetActiveBlock";
import { TraceSetBehaviorEnabledBlock } from "./blocks/TraceSetBehaviorEnabledBlock";
import { TraceGetActiveBlock } from "./blocks/TraceGetActiveBlock";
import { TraceGetChildCountBlock, TraceSetActiveChildBlock } from "./blocks/TraceChildBlocks";
import { TracePlayAnimationBlock } from "./blocks/TracePlayAnimationBlock";
import { TracePlayExternalBlock } from "./blocks/TracePlayExternalBlock";
import { TracePlayVideoBlock } from "./blocks/TracePlayVideoBlock";
import {
    TracePlayMediaBlock,
    TracePauseMediaBlock,
    TraceStopMediaBlock,
    TraceSeekMediaBlock,
    TraceSetMediaVolumeBlock,
    TraceIsMediaPlayingBlock,
    TraceGetMediaVolumeBlock,
} from "./blocks/TraceMediaBlocks";
import { TraceNavigateBlock } from "./blocks/TraceNavigateBlock";
import { TraceGetPropertyBlock } from "./blocks/TraceGetPropertyBlock";
import { TraceSetPropertyBlock } from "./blocks/TraceSetPropertyBlock";
import { TraceTimeBlock } from "./blocks/TraceTimeBlock";
import { TraceSetStateBlock } from "./blocks/TraceSetStateBlock";
import { TraceGetStateBlock } from "./blocks/TraceGetStateBlock";
import { TraceOnStateChangeBlock } from "./blocks/TraceOnStateChangeBlock";
import { TraceProximityEventBlock } from "./blocks/TraceProximityEventBlock";
import { TracePagesNextBlock, TracePagesPrevBlock, TracePagesGetActiveIndexBlock, TracePagesGetPageCountBlock } from "./blocks/TracePagesBlocks";

import { TRACE_INTERACTIVITY_EXTENSION, traceDeclarationSchema, traceOpContractForWireOp } from "./traceOpContracts";

export { TRACE_INTERACTIVITY_EXTENSION };

const MODULE = "trace";

/**
 * Register a TRACE_interactivity op mapping. Monomorphic ops also get a
 * `declarationSchema` (from traceOpContracts — the same table the exporter
 * declares sockets from), so Babylon validates a document's socket
 * declarations against exactly what the block consumes and produces.
 * Polymorphic ops (`any` sockets: getProperty/setProperty/pointer*) carry
 * none: their declared socket types vary per node, which the spec allows.
 */
function addTraceMapping(op: string, mapping: Parameters<typeof addNewInteractivityFlowGraphMapping>[2]): void {
    const contract = traceOpContractForWireOp(op);
    const declarationSchema = contract ? traceDeclarationSchema(contract) : undefined;
    addNewInteractivityFlowGraphMapping(op, TRACE_INTERACTIVITY_EXTENSION, declarationSchema ? { ...mapping, declarationSchema } : mapping);
}
let _registered = false;

export function registerTraceBlocks(): void {
    if (_registered) return;
    _registered = true;

    // ── Block factories ──
    // Static imports used here — these modules are already statically imported
    // by GraphCompiler, so dynamic imports would not create separate chunks.

    addToBlockFactory(MODULE, "PointerSetBlock", async () => PointerSetBlock);
    addToBlockFactory(MODULE, "PointerGetBlock", async () => PointerGetBlock);
    addToBlockFactory(MODULE, "PointerInterpolateBlock", async () => PointerInterpolateBlock);
    addToBlockFactory(MODULE, "TraceSetActiveBlock", async () => TraceSetActiveBlock);
    addToBlockFactory(MODULE, "TraceGetActiveBlock", async () => TraceGetActiveBlock);
    addToBlockFactory(MODULE, "TraceGetChildCountBlock", async () => TraceGetChildCountBlock);
    addToBlockFactory(MODULE, "TraceSetActiveChildBlock", async () => TraceSetActiveChildBlock);
    addToBlockFactory(MODULE, "TracePlayAnimationBlock", async () => TracePlayAnimationBlock);
    addToBlockFactory(MODULE, "TracePlayExternalBlock", async () => TracePlayExternalBlock);
    addToBlockFactory(MODULE, "TracePlayVideoBlock", async () => TracePlayVideoBlock);
    addToBlockFactory(MODULE, "TracePlayMediaBlock", async () => TracePlayMediaBlock);
    addToBlockFactory(MODULE, "TracePauseMediaBlock", async () => TracePauseMediaBlock);
    addToBlockFactory(MODULE, "TraceStopMediaBlock", async () => TraceStopMediaBlock);
    addToBlockFactory(MODULE, "TraceSeekMediaBlock", async () => TraceSeekMediaBlock);
    addToBlockFactory(MODULE, "TraceSetMediaVolumeBlock", async () => TraceSetMediaVolumeBlock);
    addToBlockFactory(MODULE, "TraceIsMediaPlayingBlock", async () => TraceIsMediaPlayingBlock);
    addToBlockFactory(MODULE, "TraceGetMediaVolumeBlock", async () => TraceGetMediaVolumeBlock);
    addToBlockFactory(MODULE, "TraceSetBehaviorEnabledBlock", async () => TraceSetBehaviorEnabledBlock);
    addToBlockFactory(MODULE, "TraceNavigateBlock", async () => TraceNavigateBlock);
    addToBlockFactory(MODULE, "TraceGetPropertyBlock", async () => TraceGetPropertyBlock);
    addToBlockFactory(MODULE, "TraceSetPropertyBlock", async () => TraceSetPropertyBlock);
    addToBlockFactory(MODULE, "TraceTimeBlock", async () => TraceTimeBlock);
    addToBlockFactory(MODULE, "TraceSetStateBlock", async () => TraceSetStateBlock);
    addToBlockFactory(MODULE, "TraceGetStateBlock", async () => TraceGetStateBlock);
    addToBlockFactory(MODULE, "TraceOnStateChangeBlock", async () => TraceOnStateChangeBlock);
    // Select/hover event blocks use Babylon's native FlowGraphMeshPickEventBlock,
    // FlowGraphPointerOverEventBlock, FlowGraphPointerOutEventBlock — no custom
    // block factories needed. KHR_node_selectability and KHR_node_hoverability
    // extensions register the proper mappings under their own namespaces.
    addToBlockFactory(MODULE, "TraceProximityEventBlock", async () => TraceProximityEventBlock);
    addToBlockFactory(MODULE, "TracePagesNextBlock", async () => TracePagesNextBlock);
    addToBlockFactory(MODULE, "TracePagesPrevBlock", async () => TracePagesPrevBlock);
    addToBlockFactory(MODULE, "TracePagesGetActiveIndexBlock", async () => TracePagesGetActiveIndexBlock);
    addToBlockFactory(MODULE, "TracePagesGetPageCountBlock", async () => TracePagesGetPageCountBlock);

    // ── Declaration mappings ──
    // Registered under the TRACE_interactivity extension namespace.
    // In KHR JSON: { op: "setActive", extension: "TRACE_interactivity" }
    // In GraphData: { op: "trace/setActive" } (converters handle translation)

    addTraceMapping("pointerSet", {
        blocks: [`${MODULE}/PointerSetBlock`],
        configuration: {
            target: { name: "target" },
            property: { name: "property" },
        },
        inputs: {
            values: { value: { name: "value" } },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("pointerInterpolate", {
        blocks: [`${MODULE}/PointerInterpolateBlock`],
        configuration: {
            target: { name: "target" },
            property: { name: "property" },
        },
        inputs: {
            values: {
                value: { name: "value" },
                duration: { name: "duration" },
                p1: { name: "p1" },
                p2: { name: "p2" },
            },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: {
                out: { name: "out" },
                done: { name: "done" },
                err: { name: "err" },
            },
        },
    });

    addTraceMapping("pointerGet", {
        blocks: [`${MODULE}/PointerGetBlock`],
        configuration: {
            target: { name: "target" },
            property: { name: "property" },
        },
        outputs: {
            values: { value: { name: "value" } },
        },
    });

    addTraceMapping("setActive", {
        blocks: [`${MODULE}/TraceSetActiveBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            values: { state: { name: "state" } },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("setBehaviorEnabled", {
        blocks: [`${MODULE}/TraceSetBehaviorEnabledBlock`],
        configuration: {
            target: { name: "target" },
            behavior: { name: "behavior" },
        },
        inputs: {
            values: { enabled: { name: "enabled" } },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("getActive", {
        blocks: [`${MODULE}/TraceGetActiveBlock`],
        configuration: {
            target: { name: "target" },
        },
        outputs: {
            values: { value: { name: "value" } },
        },
    });

    addTraceMapping("getChildCount", {
        blocks: [`${MODULE}/TraceGetChildCountBlock`],
        configuration: {
            target: { name: "target" },
        },
        outputs: {
            values: { value: { name: "value" } },
        },
    });

    addTraceMapping("setActiveChild", {
        blocks: [`${MODULE}/TraceSetActiveChildBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            values: { index: { name: "index" } },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("playAnimation", {
        blocks: [`${MODULE}/TracePlayAnimationBlock`],
        configuration: {
            target: { name: "target" },
            clip: { name: "clip" },
        },
        inputs: {
            values: {
                speed: { name: "speed" },
                loop: { name: "loop" },
            },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: {
                out: { name: "out" },
                done: { name: "done" },
            },
        },
    });

    addTraceMapping("playAnimationExternal", {
        blocks: [`${MODULE}/TracePlayExternalBlock`],
        configuration: {
            target: { name: "target" },
            src: { name: "src" },
            clip: { name: "clip" },
        },
        inputs: {
            values: {
                speed: { name: "speed" },
                loop: { name: "loop" },
            },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: {
                out: { name: "out" },
                started: { name: "started" },
                done: { name: "done" },
            },
        },
    });

    addTraceMapping("playVideo", {
        blocks: [`${MODULE}/TracePlayVideoBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            values: {
                play: { name: "play" },
                volume: { name: "volume" },
                seek: { name: "seek" },
            },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("playMedia", {
        blocks: [`${MODULE}/TracePlayMediaBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            values: {
                src: { name: "src" },
                volume: { name: "volume" },
                seek: { name: "seek" },
            },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("pauseMedia", {
        blocks: [`${MODULE}/TracePauseMediaBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("stopMedia", {
        blocks: [`${MODULE}/TraceStopMediaBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("seekMedia", {
        blocks: [`${MODULE}/TraceSeekMediaBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            values: { time: { name: "time" } },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("setMediaVolume", {
        blocks: [`${MODULE}/TraceSetMediaVolumeBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            values: { volume: { name: "volume" } },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("isMediaPlaying", {
        blocks: [`${MODULE}/TraceIsMediaPlayingBlock`],
        configuration: {
            target: { name: "target" },
        },
        outputs: {
            values: { value: { name: "value" } },
        },
    });

    addTraceMapping("getMediaVolume", {
        blocks: [`${MODULE}/TraceGetMediaVolumeBlock`],
        configuration: {
            target: { name: "target" },
        },
        outputs: {
            values: { value: { name: "value" } },
        },
    });

    addTraceMapping("navigate", {
        blocks: [`${MODULE}/TraceNavigateBlock`],
        inputs: {
            values: { url: { name: "url" } },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("getProperty", {
        blocks: [`${MODULE}/TraceGetPropertyBlock`],
        configuration: {
            target: { name: "target" },
            property: { name: "property" },
        },
        outputs: {
            values: { value: { name: "value" } },
        },
    });

    addTraceMapping("time", {
        blocks: [`${MODULE}/TraceTimeBlock`],
        outputs: {
            values: { elapsed: { name: "elapsed" } },
        },
    });

    addTraceMapping("setProperty", {
        blocks: [`${MODULE}/TraceSetPropertyBlock`],
        configuration: {
            target: { name: "target" },
            property: { name: "property" },
            transition: { name: "transition" },
        },
        inputs: {
            values: { value: { name: "value" } },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("setState", {
        blocks: [`${MODULE}/TraceSetStateBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            values: { state: { name: "state" } },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("getState", {
        blocks: [`${MODULE}/TraceGetStateBlock`],
        configuration: {
            target: { name: "target" },
        },
        outputs: {
            values: { state: { name: "state" } },
        },
    });

    addTraceMapping("onStateChange", {
        blocks: [`${MODULE}/TraceOnStateChangeBlock`],
        configuration: {
            target: { name: "target" },
        },
        outputs: {
            values: {
                prevState: { name: "prevState" },
                newState: { name: "newState" },
            },
            flows: { out: { name: "out" } },
        },
    });

    // No TRACE_interactivity mappings for onSelect/onHoverIn/onHoverOut —
    // these use the standard KHR_node_selectability and KHR_node_hoverability
    // extension namespaces with Babylon's native event blocks.

    addTraceMapping("pagesNext", {
        blocks: [`${MODULE}/TracePagesNextBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            values: {
                wrap: { name: "wrap" },
                animate: { name: "animate" },
            },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("pagesPrev", {
        blocks: [`${MODULE}/TracePagesPrevBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            values: {
                wrap: { name: "wrap" },
                animate: { name: "animate" },
            },
            flows: { in: { name: "in" } },
        },
        outputs: {
            flows: { out: { name: "out" } },
        },
    });

    addTraceMapping("pagesGetActiveIndex", {
        blocks: [`${MODULE}/TracePagesGetActiveIndexBlock`],
        configuration: {
            target: { name: "target" },
        },
        outputs: {
            values: { value: { name: "value" } },
        },
    });

    addTraceMapping("pagesGetPageCount", {
        blocks: [`${MODULE}/TracePagesGetPageCountBlock`],
        configuration: {
            target: { name: "target" },
        },
        outputs: {
            values: { value: { name: "value" } },
        },
    });

    addTraceMapping("onProximity", {
        blocks: [`${MODULE}/TraceProximityEventBlock`],
        configuration: {
            target: { name: "target" },
        },
        inputs: {
            values: { threshold: { name: "threshold" } },
        },
        outputs: {
            values: { distance: { name: "distance" } },
            flows: {
                enter: { name: "enter" },
                exit: { name: "exit" },
            },
        },
    });
}
